import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import LiveMap from "@/components/LiveMap";
import { toast } from "sonner";
import { ArrowLeft, MapPin } from "@phosphor-icons/react";
import { TID } from "@/lib/testIds";

export default function DriverActiveDelivery() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const pingRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/orders/${orderId}`);
    setData(data);
    return data;
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [orderId]);

  useEffect(() => {
    if (!data) return;
    const { order, store } = data;
    if (!["assigned", "picked_up", "delivering"].includes(order.status)) return;
    if (pingRef.current) clearInterval(pingRef.current);
    pingRef.current = setInterval(async () => {
      const cur = data.order;
      const startLat = cur.driver_lat ?? store.lat;
      const startLng = cur.driver_lng ?? store.lng;
      const dLat = (cur.delivery_lat - startLat) * 0.15;
      const dLng = (cur.delivery_lng - startLng) * 0.15;
      const nextLat = startLat + dLat + (Math.random() - 0.5) * 0.0005;
      const nextLng = startLng + dLng + (Math.random() - 0.5) * 0.0005;
      try {
        await api.post(`/driver/orders/${orderId}/location`, { lat: nextLat, lng: nextLng });
      } catch {
        /* ignore */
      }
    }, 5000);
    return () => clearInterval(pingRef.current);
  }, [data, orderId]);

  const setStatus = async (status) => {
    try {
      await api.post(`/orders/${orderId}/status`, { status });
      toast.success("Status updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  if (!data) return <div className="text-[var(--text-2)]">Loading…</div>;
  const { order, store } = data;

  const primary = () => {
    if (order.status === "assigned")
      return <button data-testid={TID.driverPickupBtn} onClick={() => setStatus("picked_up")} className="btn-primary w-full">Confirm pickup</button>;
    if (order.status === "picked_up")
      return <button data-testid={TID.driverDeliveringBtn} onClick={() => setStatus("delivering")} className="btn-primary w-full">Start delivering</button>;
    if (order.status === "delivering")
      return <button data-testid={TID.driverDeliveredBtn} onClick={() => setStatus("delivered")} className="btn-primary w-full">Mark delivered</button>;
    return <button onClick={() => navigate("/driver")} className="btn-ghost w-full">Back to dispatch</button>;
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/driver")} className="inline-flex items-center gap-2 text-sm font-bold hover:text-[var(--red)]">
        <ArrowLeft size={16} weight="bold" /> Dispatch
      </button>
      <div>
        <span className="label-eyebrow">Active · #{order.order_id.slice(-6).toUpperCase()}</span>
        <h1 className="mt-1 font-display text-4xl capitalize">{order.status.replace(/_/g, " ")}</h1>
      </div>

      <LiveMap
        store={store}
        delivery={{ lat: order.delivery_lat, lng: order.delivery_lng }}
        driver={order.driver_lat && order.driver_lng ? { lat: order.driver_lat, lng: order.driver_lng } : null}
        height={340}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-panel p-5">
          <span className="label-eyebrow">Pickup</span>
          <p className="mt-2 font-display text-xl">{store.name}</p>
          <p className="text-sm text-[var(--text-2)] inline-flex items-center gap-1 mt-1">
            <MapPin size={12} weight="fill" /> {store.address}, {store.city}
          </p>
        </div>
        <div className="card-panel p-5">
          <span className="label-eyebrow">Drop-off</span>
          <p className="mt-2 font-display text-xl inline-flex items-center gap-1">
            <MapPin size={16} weight="fill" /> {order.delivery_address}
          </p>
          <p className="text-sm text-[var(--text-2)] mt-1">
            {order.items.reduce((n, it) => n + it.quantity, 0)} items · R{order.total.toFixed(2)}
          </p>
        </div>
      </div>

      <div className="card-panel p-5">
        <span className="label-eyebrow">Items</span>
        <ul className="mt-3 divide-y divide-[var(--text)]/10">
          {order.items.map((it, i) => (
            <li key={i} className="py-2 flex justify-between text-sm font-bold">
              <span>{it.name}</span>
              <span className="text-[var(--text-2)]">× {it.quantity}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="sticky bottom-4 z-30">
        <div className="card-panel p-4">{primary()}</div>
      </div>
    </div>
  );
}
